<template>
  <div id="app" class="background-animation" :class="{ 'login-container': !isLoggedIn }">
    <!-- LOGIN -->
    <div v-if="!isLoggedIn" class="login-shell">
      <div class="login-card">
        <div class="login-brand">
          <h1 class="login-title">Dashamajig</h1>
        </div>

        <template v-if="loginMode === 'main'">
          <button type="button" class="btn-google" @click="googleLogin">
            <span class="btn-google-icon" aria-hidden="true">G</span>
            Sign in with Google
          </button>

          <div class="login-divider" aria-hidden="true"></div>

          <button type="button" class="btn-contractor-outline" @click="loginMode = 'contractor'">
            Contractor login
          </button>
        </template>

        <template v-else>
          <button type="button" class="login-back" @click="backToMainLogin">← Back</button>

          <form class="contractor-form" @submit.prevent="contractorLogin">
            <label class="field-label">
              Username
              <input
                v-model.trim="contractorUsername"
                type="text"
                autocomplete="username"
                placeholder="Your username"
                required
              />
            </label>
            <label class="field-label">
              Password
              <div class="password-wrap">
                <input
                  v-model="contractorPassword"
                  :type="showContractorPassword ? 'text' : 'password'"
                  autocomplete="current-password"
                  placeholder="Your password"
                  required
                />
                <button
                  type="button"
                  class="pw-toggle"
                  :aria-label="showContractorPassword ? 'Hide password' : 'Show password'"
                  @click="showContractorPassword = !showContractorPassword"
                >
                  {{ showContractorPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
            </label>
            <button type="submit" class="btn-contractor-submit" :disabled="contractorLoading">
              {{ contractorLoading ? 'Signing in…' : 'Sign in' }}
            </button>
          </form>
        </template>

        <p v-if="message" class="login-message" :class="{ success: messageSuccess }">{{ message }}</p>
      </div>
    </div>

    <!-- APP SHELL -->
    <div v-else class="app-shell">
      <header class="app-header">
        <nav class="nav-links">
          <router-link to="/" exact class="nav-link" active-class="active">Dashboard</router-link>
          <router-link to="/inventory" class="nav-link" active-class="active">Inventory</router-link>
          <router-link to="/starred" class="nav-link" active-class="active">Starred</router-link>
          <router-link to="/wishes" class="nav-link" active-class="active">Wishes</router-link>
          <router-link to="/customers" class="nav-link" active-class="active">Contacts</router-link>
          <router-link to="/tags" class="nav-link" active-class="active">Tags</router-link>
          <router-link to="/companies" class="nav-link" active-class="active">Companies</router-link>
          <router-link to="/estimates" class="nav-link" active-class="active">Conduit</router-link>
        </nav>
        <div class="user-controls">
          <WhatsNewOrb />
          <div v-if="canChangePassword" class="user-menu">
            <button type="button" class="user-menu-trigger" aria-haspopup="true">
              <span class="greeting">Sup {{ displayName }}</span>
              <span class="user-menu-chevron" aria-hidden="true">▾</span>
            </button>
            <div class="user-menu-panel" role="menu">
              <button type="button" role="menuitem" class="user-menu-item" @click="openPasswordModal">
                <span class="user-menu-item-icon" aria-hidden="true">
                  <svg class="pw-key-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <defs>
                      <linearGradient id="pwKeyGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stop-color="#f472b6" />
                        <stop offset="50%" stop-color="#c084fc" />
                        <stop offset="100%" stop-color="#22d3ee" />
                      </linearGradient>
                    </defs>
                    <circle cx="7.5" cy="7.5" r="4.25" stroke="url(#pwKeyGrad)" stroke-width="1.75" />
                    <path
                      d="M10.8 10.8 L19.2 19.2 M19.2 19.2 V16.2 M19.2 19.2 H16.2"
                      stroke="url(#pwKeyGrad)"
                      stroke-width="1.75"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                Change password
              </button>
            </div>
          </div>
          <span v-else class="greeting">Sup {{ displayName }}</span>
          <button type="button" class="logout-button" @click="handleLogout">Log out</button>
        </div>
      </header>

      <main class="app-content">
        <router-view v-slot="{ Component, route }">
          <keep-alive include="CustomersPage">
            <component
              :is="Component"
              v-bind="route.params"
              :username="username"
              @logout="handleLogout"
            />
          </keep-alive>
        </router-view>
      </main>
    </div>

    <footer class="app-site-footer" aria-label="Credits">
      Made by Kevin in his sleep
      <i class="fa-solid fa-moon app-site-footer-moon" aria-hidden="true"></i>
    </footer>

    <!-- Change password modal (contractor / manual login) -->
    <div v-if="showPasswordModal" class="pw-modal-backdrop" @click.self="closePasswordModal">
      <div class="pw-modal pw-modal--synth" role="dialog" aria-labelledby="pw-modal-title">
        <div class="pw-modal-glow" aria-hidden="true"></div>
        <header class="pw-modal-header">
          <h2 id="pw-modal-title">Change password</h2>
          <button type="button" class="pw-modal-close" aria-label="Close" @click="closePasswordModal">✕</button>
        </header>
        <form class="pw-modal-body" @submit.prevent="submitPasswordChange">
          <p class="pw-modal-intro">Enter your current password, then your new password twice.</p>

          <label class="field-label">
            Current password
            <div class="password-wrap">
              <input
                v-model="pwForm.current"
                :type="showPw.current ? 'text' : 'password'"
                autocomplete="current-password"
                required
              />
              <button type="button" class="pw-toggle" @click="showPw.current = !showPw.current">
                {{ showPw.current ? 'Hide' : 'Show' }}
              </button>
            </div>
          </label>

          <label class="field-label">
            New password
            <div class="password-wrap">
              <input
                v-model="pwForm.next"
                :type="showPw.next ? 'text' : 'password'"
                autocomplete="new-password"
                minlength="6"
                required
              />
              <button type="button" class="pw-toggle" @click="showPw.next = !showPw.next">
                {{ showPw.next ? 'Hide' : 'Show' }}
              </button>
            </div>
          </label>

          <label class="field-label">
            Confirm new password
            <div class="password-wrap">
              <input
                v-model="pwForm.confirm"
                :type="showPw.confirm ? 'text' : 'password'"
                autocomplete="new-password"
                minlength="6"
                required
              />
              <button type="button" class="pw-toggle" @click="showPw.confirm = !showPw.confirm">
                {{ showPw.confirm ? 'Hide' : 'Show' }}
              </button>
            </div>
          </label>

          <p v-if="pwError" class="pw-feedback error">{{ pwError }}</p>
          <p v-if="pwSuccess" class="pw-feedback success">{{ pwSuccess }}</p>

          <div class="pw-modal-actions">
            <button type="button" class="btn-ghost" @click="closePasswordModal">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="pwSaving">
              {{ pwSaving ? 'Saving…' : 'Update password' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from './services/AuthService'
import WhatsNewOrb from './components/WhatsNewOrb.vue'
import { tokenLooksValid } from './utils/session'

export default {
  name: 'App',
  components: { WhatsNewOrb },
  data() {
    return {
      username: '',
      message: '',
      messageSuccess: false,
      isLoggedIn: false,
      loginMode: 'main',
      contractorUsername: '',
      contractorPassword: '',
      showContractorPassword: false,
      contractorLoading: false,
      showPasswordModal: false,
      pwForm: { current: '', next: '', confirm: '' },
      showPw: { current: false, next: false, confirm: false },
      pwError: '',
      pwSuccess: '',
      pwSaving: false,
      sessionAuthProvider: ''
    }
  },
  computed: {
    canChangePassword() {
      return this.sessionAuthProvider === 'contractor'
    },
    displayName() {
      const u = (this.username || '').trim()
      if (!u) return 'friend'
      const local = u.includes('@') ? u.split('@')[0] : u
      return local.charAt(0).toUpperCase() + local.slice(1)
    }
  },
  async created() {
    this.stripSensitiveQueryParams()

    if (sessionStorage.getItem('gmail_connected') === '1') {
      sessionStorage.removeItem('gmail_connected')
    }

    let token = localStorage.getItem('token')
    let storedUsername = localStorage.getItem('username')

    if (token && !tokenLooksValid(token)) {
      localStorage.removeItem('token')
      token = null
    }

    if (!token) {
      try {
        const { data, status } = await AuthService.claimGoogleSession()
        if (status === 200 && data?.token) {
          this.persistSession({
            token: data.token,
            username: data.username,
            authProvider: data.auth_provider || 'google',
            gmailAvailable: data.gmail_available !== false
          })
          token = data.token
          storedUsername = data.username
        } else if (status === 200 && data?.gmail_connected) {
          sessionStorage.setItem('gmail_connected', '1')
        }
      } catch {
        /* no pending Google handoff */
      }
    }

    if (token && storedUsername) {
      if (!localStorage.getItem('authProvider')) {
        if (storedUsername.includes('@chibitronics.com')) {
          localStorage.setItem('authProvider', 'google')
          localStorage.setItem('gmailAvailable', 'true')
        } else {
          localStorage.setItem('authProvider', 'contractor')
          localStorage.setItem('gmailAvailable', 'false')
        }
      }
      this.sessionAuthProvider = localStorage.getItem('authProvider') || 'google'
      this.isLoggedIn = true
      this.username = storedUsername
    }

    window.addEventListener('dashamajig:session-expired', this.handleSessionExpired)
  },
  beforeUnmount() {
    window.removeEventListener('dashamajig:session-expired', this.handleSessionExpired)
  },
  methods: {
    handleSessionExpired() {
      if (!this.isLoggedIn) return
      this.message = 'Your session expired. Please sign in again.'
      this.messageSuccess = false
      this.handleLogout()
    },
    stripSensitiveQueryParams() {
      const params = new URLSearchParams(window.location.search)
      const sensitive = ['token', 'email', 'auth_code']
      let changed = false
      sensitive.forEach((key) => {
        if (params.has(key)) {
          params.delete(key)
          changed = true
        }
      })
      if (!changed) return
      const path = window.location.pathname || '/'
      const qs = params.toString()
      window.history.replaceState({}, document.title, qs ? `${path}?${qs}` : path)
    },
    persistSession({ token, username, authProvider, gmailAvailable }) {
      localStorage.setItem('token', token)
      localStorage.setItem('username', username)
      localStorage.setItem('authProvider', authProvider)
      localStorage.setItem('gmailAvailable', gmailAvailable ? 'true' : 'false')
      this.sessionAuthProvider = authProvider || 'google'
      this.username = username
      this.isLoggedIn = true
      this.message = ''
      this.messageSuccess = false
    },
    googleLogin() {
      window.location.href = 'https://dashamajig.chibitronics.com/api/auth/google/login'
    },
    backToMainLogin() {
      this.loginMode = 'main'
      this.message = ''
      this.contractorPassword = ''
    },
    async contractorLogin() {
      this.message = ''
      this.messageSuccess = false
      this.contractorLoading = true
      try {
        const { data } = await AuthService.login(this.contractorUsername, this.contractorPassword)
        this.persistSession({
          token: data.token,
          username: data.username || this.contractorUsername,
          authProvider: data.auth_provider || 'contractor',
          gmailAvailable: !!data.gmail_available
        })
        this.contractorPassword = ''
      } catch (err) {
        const msg = err.response?.data?.error || 'Invalid username or password.'
        this.message = msg
        this.messageSuccess = false
      } finally {
        this.contractorLoading = false
      }
    },
    handleLogout() {
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      localStorage.removeItem('authProvider')
      localStorage.removeItem('gmailAvailable')
      this.sessionAuthProvider = ''
      this.isLoggedIn = false
      this.username = ''
      this.loginMode = 'main'
    },
    openPasswordModal() {
      this.pwForm = { current: '', next: '', confirm: '' }
      this.showPw = { current: false, next: false, confirm: false }
      this.pwError = ''
      this.pwSuccess = ''
      this.showPasswordModal = true
    },
    closePasswordModal() {
      this.showPasswordModal = false
      this.pwForm = { current: '', next: '', confirm: '' }
      this.pwError = ''
    },
    async submitPasswordChange() {
      this.pwError = ''
      this.pwSuccess = ''
      if (this.pwForm.next !== this.pwForm.confirm) {
        this.pwError = 'New password and confirmation do not match.'
        return
      }
      if (this.pwForm.next.length < 6) {
        this.pwError = 'New password must be at least 6 characters.'
        return
      }
      this.pwSaving = true
      try {
        const { data } = await AuthService.changePassword({
          current_password: this.pwForm.current,
          password: this.pwForm.next,
          password_confirmation: this.pwForm.confirm
        })
        this.pwSuccess = data.message || 'Password updated.'
        this.pwForm = { current: '', next: '', confirm: '' }
        setTimeout(() => this.closePasswordModal(), 1800)
      } catch (err) {
        const errs = err.response?.data?.errors
        this.pwError = Array.isArray(errs) ? errs.join(' ') : (err.response?.data?.error || 'Could not update password.')
      } finally {
        this.pwSaving = false
      }
    }
  }
}
</script>

<style>
* { margin: 0; padding: 0; box-sizing: border-box; }

body, html {
  height: 100%;
  margin: 0;
  font-family: 'Poppins', sans-serif;
  background: linear-gradient(270deg, #0f0c29, #302b63, #24243e);
  background-size: 400% 400%;
  animation: gradientBG 15s ease infinite;
  color: #ffffff;
  overflow-y: auto;
}
@keyframes gradientBG {
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

.login-container {
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem 3.5rem;
}

.login-shell { width: 100%; max-width: 420px; }

.login-card {
  background: linear-gradient(145deg, rgba(44, 62, 80, 0.92), rgba(26, 32, 56, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  padding: 2rem 1.75rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45), 0 0 0 1px rgba(26, 188, 156, 0.08);
}

.login-brand { text-align: center; margin-bottom: 1.5rem; }
.login-title {
  font-size: 1.85rem;
  font-weight: 700;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, #a5f3fc, #34d399);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.btn-google {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  background: #4285f4;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;
  margin: 0;
}
.btn-google:hover { background: #3367d6; transform: translateY(-1px); }
.btn-google-icon {
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 4px;
  background: #fff;
  color: #4285f4;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.95rem;
}

.login-divider {
  height: 1px;
  margin: 1.25rem 0;
  background: rgba(255, 255, 255, 0.15);
}

.btn-contractor-outline {
  width: 100%;
  padding: 0.7rem 1rem;
  background: transparent;
  border: 1px solid rgba(167, 139, 250, 0.55);
  border-radius: 8px;
  color: #e9d5ff;
  font-weight: 600;
  cursor: pointer;
  margin: 0;
  transition: background 0.2s, border-color 0.2s;
}
.btn-contractor-outline:hover {
  background: rgba(167, 139, 250, 0.12);
  border-color: rgba(167, 139, 250, 0.85);
  transform: none;
}

.login-back {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0;
  margin: 0 0 0.75rem;
}
.login-back:hover { color: #fff; transform: none; background: none; }

.contractor-form { margin-top: 0.75rem; }
.field-label {
  display: block;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.85rem;
}
.field-label input {
  display: block;
  width: 100%;
  margin-top: 0.35rem;
  padding: 0.65rem 0.75rem;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #fff;
}
.password-wrap {
  display: flex;
  align-items: stretch;
  margin-top: 0.35rem;
  gap: 0.35rem;
}
.password-wrap input { flex: 1; margin-top: 0; }
.pw-toggle {
  flex-shrink: 0;
  padding: 0 0.65rem;
  font-size: 0.75rem;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.85);
  margin: 0;
  transform: none;
}
.pw-toggle:hover { background: rgba(255, 255, 255, 0.14); transform: none; }

.btn-contractor-submit {
  width: 100%;
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, #7c3aed, #a855f7);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
}
.btn-contractor-submit:hover:not(:disabled) {
  background: linear-gradient(135deg, #6d28d9, #9333ea);
  transform: translateY(-1px);
}
.btn-contractor-submit:disabled { opacity: 0.65; cursor: wait; }

.login-message {
  margin-top: 1rem;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  font-size: 0.88rem;
  text-align: center;
  color: #fecaca;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.35);
}
.login-message.success {
  color: #bbf7d0;
  background: rgba(34, 197, 94, 0.12);
  border-color: rgba(34, 197, 94, 0.35);
}

.app-shell { display: flex; flex-direction: column; }

.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  background: #2c3e50;
  padding: 0.5rem 1rem;
}
.nav-links { display: flex; flex-wrap: wrap; }
.nav-link {
  margin-right: 1rem;
  color: #ecf0f1;
  text-decoration: none;
  font-weight: 500;
}
.nav-link.active { border-bottom: 2px solid #1abc9c; }
.nav-link:hover { color: #1abc9c; }

.user-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.greeting { font-weight: 500; }

/* Contractor account menu */
.user-menu {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.user-menu-trigger {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.65rem;
  margin: 0;
  background: rgba(167, 139, 250, 0.12);
  border: 1px solid rgba(236, 72, 153, 0.35);
  border-radius: 999px;
  color: #f5d0fe;
  cursor: default;
  box-shadow: 0 0 12px rgba(236, 72, 153, 0.15);
  transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
}
.user-menu:hover .user-menu-trigger {
  border-color: rgba(34, 211, 238, 0.65);
  background: rgba(34, 211, 238, 0.1);
  box-shadow: 0 0 18px rgba(34, 211, 238, 0.25);
}
.user-menu-chevron {
  font-size: 0.7rem;
  color: rgba(250, 204, 255, 0.85);
  transition: transform 0.2s;
}
.user-menu:hover .user-menu-chevron {
  transform: rotate(180deg);
  color: #67e8f9;
}
.user-menu-panel {
  position: absolute;
  top: calc(100% + 0.45rem);
  right: 0;
  min-width: 11.5rem;
  padding: 0.35rem;
  background: linear-gradient(165deg, rgba(30, 27, 75, 0.98), rgba(15, 23, 42, 0.99));
  border: 1px solid rgba(236, 72, 153, 0.45);
  border-radius: 10px;
  box-shadow:
    0 0 0 1px rgba(34, 211, 238, 0.12),
    0 12px 40px rgba(0, 0, 0, 0.5),
    0 0 24px rgba(167, 139, 250, 0.2);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-6px);
  transition: opacity 0.18s, transform 0.18s, visibility 0.18s;
  z-index: 100;
}
.user-menu:hover .user-menu-panel,
.user-menu:focus-within .user-menu-panel {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}
.user-menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 0.75rem;
  margin: 0;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #e9d5ff;
  font-size: 0.88rem;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.user-menu-item:hover {
  background: linear-gradient(90deg, rgba(236, 72, 153, 0.2), rgba(34, 211, 238, 0.15));
  color: #fff;
  transform: none;
}
.user-menu-item-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.pw-key-icon {
  width: 1.2rem;
  height: 1.2rem;
  animation: pw-key-pulse 2.2s ease-in-out infinite;
}
.pw-key-icon circle,
.pw-key-icon path {
  animation: pw-key-stroke 2.2s ease-in-out infinite;
}
@keyframes pw-key-pulse {
  0%, 100% {
    filter: drop-shadow(0 0 3px rgba(244, 114, 182, 0.55));
    transform: scale(1);
  }
  50% {
    filter: drop-shadow(0 0 10px rgba(34, 211, 238, 0.9));
    transform: scale(1.08);
  }
}
@keyframes pw-key-stroke {
  0%, 100% { opacity: 0.85; }
  50% { opacity: 1; }
}
.logout-button {
  background: #e74c3c;
  border: none;
  padding: 0.4rem 0.8rem;
  color: white;
  cursor: pointer;
  border-radius: 4px;
  margin: 0;
}
.logout-button:hover { background: #c0392b; transform: none; }

.app-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: visible;
  padding: 1rem;
  padding-bottom: 2.75rem;
}

.app-site-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 60;
  padding: 0.4rem 1rem;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.55);
  text-align: center;
  background: rgba(15, 12, 41, 0.5);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(6px);
  pointer-events: none;
}
.app-site-footer-moon {
  margin-left: 0.4rem;
  color: rgba(250, 218, 158, 0.75);
}

/* Password change modal — synthwave */
.pw-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 200;
  background: radial-gradient(ellipse at 50% 0%, rgba(236, 72, 153, 0.12), transparent 55%),
    rgba(2, 6, 23, 0.82);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.pw-modal {
  position: relative;
  width: 100%;
  max-width: 440px;
  background: linear-gradient(165deg, rgba(30, 27, 75, 0.97), rgba(15, 23, 42, 0.99));
  border: 1px solid rgba(236, 72, 153, 0.4);
  border-radius: 16px;
  box-shadow:
    0 0 0 1px rgba(34, 211, 238, 0.15),
    0 28px 80px rgba(0, 0, 0, 0.55),
    0 0 48px rgba(167, 139, 250, 0.18);
  overflow: hidden;
}
.pw-modal-glow {
  position: absolute;
  inset: -40% -20% auto;
  height: 55%;
  background: linear-gradient(180deg, rgba(236, 72, 153, 0.15), transparent);
  pointer-events: none;
}
.pw-modal-header {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.15rem 1.25rem 1rem;
  border-bottom: 1px solid rgba(236, 72, 153, 0.2);
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.2), transparent);
}
.pw-modal-header h2 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  background: linear-gradient(135deg, #f9a8d4, #a5f3fc);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.pw-modal-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  margin: 0;
}
.pw-modal-close:hover { color: #fff; transform: none; background: none; }

.pw-modal-body { padding: 1.25rem; }
.pw-modal-intro {
  margin: 0 0 1rem;
  font-size: 0.88rem;
  color: rgba(226, 232, 240, 0.75);
  line-height: 1.45;
}
.pw-feedback {
  margin: 0.75rem 0 0;
  padding: 0.5rem 0.65rem;
  border-radius: 6px;
  font-size: 0.85rem;
}
.pw-feedback.error {
  color: #fecaca;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
}
.pw-feedback.success {
  color: #bbf7d0;
  background: rgba(34, 197, 94, 0.12);
  border: 1px solid rgba(34, 197, 94, 0.3);
}
.pw-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
}
.btn-ghost {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin: 0;
}
.btn-ghost:hover { background: rgba(255, 255, 255, 0.08); transform: none; }
.pw-modal--synth .field-label input:focus {
  outline: none;
  border-color: rgba(34, 211, 238, 0.7);
  box-shadow: 0 0 0 2px rgba(236, 72, 153, 0.2), 0 0 14px rgba(34, 211, 238, 0.25);
}
.btn-primary {
  background: linear-gradient(135deg, #db2777, #7c3aed 50%, #0891b2);
  border: none;
  color: #fff;
  padding: 0.5rem 1.1rem;
  border-radius: 8px;
  box-shadow: 0 0 16px rgba(236, 72, 153, 0.35);
  font-weight: 600;
  margin: 0;
}
.btn-primary:hover:not(:disabled) { filter: brightness(1.08); transform: none; }
.btn-primary:disabled { opacity: 0.6; }
</style>
